import React, { FC, MouseEvent, useCallback } from "react";
import { connect } from "react-redux";

import { faCircleXmark } from "@fortawesome/pro-solid-svg-icons/faCircleXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useIntl } from "react-intl";

import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { TextField } from "@mapmycustomers/ui";

import CardType from "@app/enum/dashboard/CardType";
import useCard from "@app/scene/dashboard/utils/useCard";
import { getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";

export interface CustomTitleProps {
  disabled?: boolean;
  onChange?: (value: string | undefined) => void;
  scope: Scope;
  stages: Stage[];
  type: CardType;
  value?: string;
}

const CustomTitle: FC<CustomTitleProps> = ({ disabled, onChange, scope, stages, type, value }) => {
  const intl = useIntl();
  const form = useFormInstance();

  const { getCardPlainTitle } = useCard(type);

  let canonicalTitle = "";
  try {
    canonicalTitle = getCardPlainTitle(intl, {
      configuration: form.getFieldsValue(),
      extraProps: { stages },
      scope,
    }).toUpperCase();
  } catch {
    // do nothing, this might happen when form is not fully initialized
  }

  const handleChange = useCallback(
    (value: string) => {
      if (value.trim() === canonicalTitle) {
        onChange?.(undefined);
      } else {
        onChange?.(value);
      }
    },
    [canonicalTitle, onChange]
  );

  const handleClearClick = useCallback(
    (e: MouseEvent<Element>) => {
      e.preventDefault();
      e.stopPropagation();
      onChange?.(undefined);
    },
    [onChange]
  );

  return (
    <div>
      <TextField
        allowClear={
          value
            ? {
                clearIcon: <FontAwesomeIcon icon={faCircleXmark} onClick={handleClearClick} />,
              }
            : {
                clearIcon: (
                  // have to use an empty tag here, otherwise TextField loses a focus when suffix is toggled
                  <span />
                ),
              }
        }
        locked={disabled}
        maxLength={40}
        onChange={handleChange}
        value={value ?? canonicalTitle}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  stages: Object.values(getFunnelStages(state)).flat(),
});

export default connect(mapStateToProps)(CustomTitle);
